import {
  Image,
  ImageSourcePropType,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Toast from "react-native-toast-message";
import { useTranslation } from "react-i18next";
import Centered from "../../../../../shared/Centered";
import { inset } from "../../../../../utils/helpers/css";
import { BGHorDivider, BGTimerWrapper, ChipPng } from "../../../../../assets";
import BGPlayPaidBtn from "../../../../../assets/bonusGames/play.png";
import { IMiniGameProps } from "../../../../../../typings/common";
import { useBonusCountdown } from "../../../../../utils/hooks/useBonusCountdown";

type BonusGameCardProps = Pick<
  IMiniGameProps,
  "isSmall" | "onStartGame" | "config" | "miniGameData" | "playerLevel"
> & {
  titleSource: ImageSourcePropType;
  thumbSource: ImageSourcePropType;
  launchPath: string;
  cooldownHours: number;
  freePlayTitleFontSize: number;
  infoPaddingVertical: number;
  hideDividerOnSmall?: boolean;
};

function BonusGameCard({
  isSmall,
  onStartGame,
  config: { url, token, language },
  miniGameData,
  playerLevel,
  titleSource,
  thumbSource,
  launchPath,
  cooldownHours,
  freePlayTitleFontSize,
  infoPaddingVertical,
  hideDividerOnSmall = false,
}: BonusGameCardProps) {
  const { t } = useTranslation();
  const isPlayMiniGameDisabled =
    !miniGameData ||
    (!miniGameData?.is_bonus_ready && (playerLevel ?? 0) < 10);
  const showPaidPlayState =
    !!miniGameData && (playerLevel ?? 0) >= 10 && !miniGameData?.is_bonus_ready;
  const paidPlayStake = miniGameData?.stake ?? 500;

  const collectedAt = miniGameData?.collected_at;
  const { timeText } = useBonusCountdown(collectedAt, cooldownHours);
  const freePlayIn = collectedAt ? timeText : "00:00:00";

  const canShowDivider = !hideDividerOnSmall || !isSmall;

  return (
    <View
      style={{
        width: isSmall ? "100%" : undefined,
        gap: isSmall ? 2 : 12,
        alignItems: "center",
        flex: isSmall ? undefined : 1,
      }}
    >
      <Image
        source={titleSource}
        resizeMode="contain"
        style={{
          width: 180,
          height: 40,
        }}
      />

      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          width: isSmall ? "100%" : "86%",
          minHeight: 110,
          gap: 10,
        }}
      >
        <Image
          source={thumbSource}
          resizeMode="contain"
          style={{
            width: isSmall ? 90 : 80,
            height: isSmall ? 90 : 80,
          }}
        />

        <View
          style={{
            flex: 1,
            alignItems: "center",
            gap: 2,
            paddingVertical: infoPaddingVertical,
            paddingRight: isSmall ? 4 : 12,
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              fontSize: freePlayTitleFontSize,
              color: "white",
              textAlign: "center",
            }}
          >
            {t("T_SITE_MODAL_BONUS_FREE_PLAY")}
          </Text>

          <View
            style={{
              width: isSmall ? 165 : 140,
              height: isSmall ? 54 : 38,
              position: "relative",
            }}
          >
            <Image
              source={BGTimerWrapper}
              resizeMode="stretch"
              style={{
                width: "100%",
                height: "100%",
              }}
            />
            <Centered style={{ ...inset(0), position: "absolute" }}>
              <Text
                style={{
                  color: "white",
                  fontWeight: "900",
                  fontSize: isSmall ? 12 : 11,
                  textAlign: "center",
                }}
                numberOfLines={1}
              >
                {freePlayIn}
              </Text>
            </Centered>
          </View>

          <TouchableOpacity
            activeOpacity={0.75}
            onPress={() => {
              if (isPlayMiniGameDisabled) {
                if ((playerLevel ?? 0) < 10) {
                  Toast.show({
                    type: "info",
                    text1: t("T_SITE_MODAL_GAME_PLAY_LEVEL_ERROR").replace(
                      "%%",
                      "10",
                    ),
                    position: "bottom",
                    visibilityTime: 3000,
                  });
                }
                return;
              }
              onStartGame(
                `${launchPath}?gameApiUrl=${url}&token=${token}&language=${language}`,
              );
            }}
          >
            <View style={styles.playButtonWrapper}>
              <Image
                source={BGPlayPaidBtn}
                resizeMode="contain"
                style={{
                  width: isSmall ? 165 : 145,
                  aspectRatio: 2.3,
                  maxWidth: isSmall ? 165 : 145,
                  height: isSmall ? 46 : 40,
                  opacity: isPlayMiniGameDisabled ? 0.6 : 1,
                }}
              />
              <View style={styles.playButtonOverlay}>
                {showPaidPlayState ? (
                  <View style={styles.paidRow}>
                    <Text style={styles.playText}>{t("T_SITE_MODAL_BONUS_PLAY")}</Text>
                    <View style={styles.amountRow}>
                      <Image source={ChipPng} style={styles.stakeChip} />
                      <Text style={styles.stakeText}>{paidPlayStake}</Text>
                      <Text style={styles.plusText}>+</Text>
                    </View>
                  </View>
                ) : (
                  <Text style={styles.playText}>{t("T_SITE_MODAL_BONUS_PLAY")}</Text>
                )}
              </View>
            </View>
          </TouchableOpacity>
        </View>
      </View>

      {canShowDivider && (
        <Image
          source={BGHorDivider}
          resizeMode="contain"
          style={{
            width: "100%",
            aspectRatio: 27.5,
            maxWidth: isSmall ? 220 : undefined,
          }}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  playButtonWrapper: {
    position: "relative",
  },
  playButtonOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
  },
  playText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 11,
    textTransform: "uppercase",
  },
  stakeChip: {
    width: 11,
    height: 11,
    transform: [{ translateY: 1 }],
  },
  stakeText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 11,
    marginLeft: -1,
  },
  plusText: {
    color: "#fff",
    fontWeight: "bold",
    fontSize: 11,
    marginLeft: -2,
    transform: [{ translateY: -0.5 }],
  },
  paidRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  amountRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
});

export default BonusGameCard;
